/**
 * MonsterList Component
 *
 * Displays available monsters for selection during monster placement phase.
 * Shows all 10 monsters with visual feedback for which ones are available/selected/placed.
 *
 * Used by:
 * - MonsterPlacementPhase (for selecting which monster to place)
 *
 * Responsibilities:
 * - Display grid of monster options
 * - Handle monster selection
 * - Show which monsters are already placed
 * - Show currently selected monster
 * - Provide hover feedback
 */

import React, { useMemo, useState, useCallback } from 'react';
import { getAllMonsters } from '../../utils/constants';
import type { MonsterDefinition } from '../../types/monster.types';
import type { SelectedMonster } from '../../types/monster.types';
import type { Theme } from '../../types/game.types';
import MonsterCircle from './MonsterCircle';
import MonsterInfo from './MonsterInfo';
import './MonsterList.css';

export interface MonsterListProps {
    /** Currently selected monster (for placement); used to highlight in list */
    selectedMonster: SelectedMonster | null;
    /** Called when user selects or deselects a monster (pass null to deselect) */
    onMonsterSelect: (monster: MonsterDefinition | null) => void;
    /** Monster type IDs (1–10) already placed this game; these are shown dimmed and not clickable */
    placedMonsterTypeIds: number[];
    /** Current theme for monster images */
    theme: Theme;
    /** If false, selection is disabled and "Waiting for opponent" is shown */
    isMyTurn: boolean;
}

export const MonsterList: React.FC<MonsterListProps> = ({
    selectedMonster,
    onMonsterSelect,
    placedMonsterTypeIds,
    theme,
    isMyTurn,
}) => {
    const [hovered, setHovered] = useState<MonsterDefinition | null>(null);
    const [imageDirection, setImageDirection] = useState<'up' | 'down'>('up');

    const availableMonsters = useMemo(() => getAllMonsters(), []);

    const isPlaced = useCallback(
        (monsterId: number) => placedMonsterTypeIds.includes(monsterId),
        [placedMonsterTypeIds]
    );

    const handleClick = useCallback(
        (monster: MonsterDefinition) => {
            if (!isMyTurn) return;
            if (isPlaced(monster.id)) return;
            // Toggle: if already selected, deselect
            if (selectedMonster?.id === monster.id) {
                onMonsterSelect(null);
            } else {
                onMonsterSelect(monster);
            }
        },
        [isMyTurn, isPlaced, selectedMonster?.id, onMonsterSelect]
    );

    const handleMouseEnter = useCallback((monster: MonsterDefinition) => {
        setHovered(monster);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHovered(null);
    }, []);

    const toggleImageDirection = useCallback(() => {
        setImageDirection((d) => (d === 'up' ? 'down' : 'up'));
    }, []);

    const getStyle = useCallback(
        (monster: MonsterDefinition): React.CSSProperties => {
            const opacity = hovered?.id === monster.id ? 0.75 : 1;

            if (isPlaced(monster.id)) {
                return {
                    filter: 'brightness(0.5)',
                    opacity,
                };
            }

            if (selectedMonster?.id === monster.id) {
                return {
                    filter: 'brightness(1.5)',
                    opacity,
                };
            }

            return {
                filter: 'brightness(1)',
                opacity,
            };
        },
        [hovered, selectedMonster?.id, isPlaced]
    );

    return (
        <div className="monster_list_container">
            <div className="monster_option_container">
                <div className="monster_option_title">
                    <p className="monster_option_title_text">
                        {isMyTurn ? 'Select a monster to place.' : 'Waiting for opponent to place'}
                    </p>
                </div>
                <div className="monster_option_body">
                    {availableMonsters.map((monster) => (
                        (() => {
                            const style = getStyle(monster);
                            const disabled = !isMyTurn || isPlaced(monster.id);

                            return (
                                <MonsterCircle
                                    key={monster.id}
                                    monster={monster}
                                    theme={theme}
                                    disabled={disabled}
                                    onClick={() => handleClick(monster)}
                                    onMouseEnter={() => handleMouseEnter(monster)}
                                    onMouseLeave={handleMouseLeave}
                                    style={{
                                        cursor: disabled ? 'default' : 'pointer',
                                        opacity: style.opacity ?? 1,
                                    }}
                                    imageStyle={{
                                        filter: (style as React.CSSProperties).filter,
                                    }}
                                />
                            );
                        })()
                    ))}
                </div>
            </div>

            <MonsterInfo
                theme={theme}
                monster={
                    selectedMonster ?
                        { type: selectedMonster.type, name: selectedMonster.name }
                    :   null
                }
                imageDirection={imageDirection}
                onToggleImageDirection={toggleImageDirection}
            />
        </div>
    );
};

export default MonsterList;
